import React, { useState, useMemo, useEffect } from 'react';
import { Box, VStack, HStack, Text, Input, createListCollection } from '@chakra-ui/react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { useGetRoomSlotsQuery, useCreateBookingMutation, useGetCustomerQuery } from '../../api';
import { useRoomById } from '../../hooks/useRooms';
import PageHeader from '../../components/base/PageHeader';
import LoadingState from '../../components/base/LoadingState';
import MeetingRoomPreview from '../../components/office/MeetingRoomPreview';
import BaseSelect from '../../components/base/BaseSelect';
import Button from '../../components/base/Button';
import { alertManager } from '../../utils/alertManager';
import { modalManager } from '../../utils/modalManager';
import { calculateTimeDifference, formatDateToYYYYMMDD, formatDuration } from '../../utils/dateUtils';
import { canPreviewCoworking } from './coworkingPreview';

const MeetingRoomBookingPage: React.FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const roomId = Number(searchParams.get('roomId'));
    const [selectedDate, setSelectedDate] = useState<string>(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return formatDateToYYYYMMDD(tomorrow);
    });

    // Получаем текущую дату в формате YYYY-MM-DD для ограничения выбора
    const todayDate = useMemo(() => formatDateToYYYYMMDD(new Date()), []);

    // Форматируем дату для запроса (YYYY-MM-DD)
    const formattedDate = useMemo(() => {
        if (!selectedDate) return undefined;
        return selectedDate;
    }, [selectedDate]);

    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isCoworking = pathname.startsWith('/office/coworking');
    const roomsPath = isCoworking ? '/office/coworking' : '/office/meeting-rooms';

    // Запрос данных о пользователе (для проверки баланса)
    const { data: customer, isLoading: isCustomerLoading } = useGetCustomerQuery(undefined, {
        refetchOnMountOrArgChange: true,
    });

    useEffect(() => {
        if (isCoworking && customer && !canPreviewCoworking(customer.id)) {
            navigate('/office', { replace: true });
        }
    }, [isCoworking, customer, navigate]);

    // Запрос данных о комнате
    const { room } = useRoomById(roomId);

    // Запрос слотов времени
    const { data: slots, isLoading: isSlotsLoading } = useGetRoomSlotsQuery(
        {
            roomId: Number(roomId),
            date: formattedDate || '',
        },
        {
            skip: !roomId || !formattedDate,
        },
    );

    // Mutation для создания бронирования
    const [createBooking, { isLoading: isBookingLoading }] = useCreateBookingMutation();

    // Новый механизм выбора через селекты
    const [selectedStartTime, setSelectedStartTime] = useState<string>('');
    const [selectedEndTime, setSelectedEndTime] = useState<string>('');

    // Коллекция для селекта "Начало" - все уникальные времена начала слотов
    const startTimeCollection = useMemo(() => {
        if (!slots) {
            return createListCollection<{ value: string; label: string }>({ items: [] });
        }

        const uniqueStartTimes = Array.from(new Set(slots.map((slot) => slot.start_time))).sort();

        return createListCollection({
            items: uniqueStartTimes.map((time) => ({
                value: time,
                label: time,
            })),
        });
    }, [slots]);

    // Коллекция для селекта "Конец" - времена окончания слотов, которые больше выбранного начала
    // Показываем только до первого забронированного слота
    const endTimeCollection = useMemo(() => {
        if (!slots || !selectedStartTime) {
            return createListCollection<{ value: string; label: string }>({ items: [] });
        }

        // Находим все слоты, которые начинаются с выбранного времени или позже
        const slotsFromStart = slots
            .filter((slot) => {
                // Показываем только слоты, которые начинаются с выбранного времени или позже
                return slot.start_time >= selectedStartTime;
            })
            .sort((a, b) => {
                // Сортируем по времени начала, затем по времени окончания
                if (a.start_time !== b.start_time) {
                    return a.start_time.localeCompare(b.start_time);
                }
                return a.end_time.localeCompare(b.end_time);
            });

        // Находим первый забронированный слот
        const firstBookedSlot = slotsFromStart.find((slot) => !slot.is_available);

        // Если есть забронированный слот, показываем только слоты до него (не включая его)
        // Это означает, что показываем только времена окончания, которые меньше времени начала первого забронированного слота
        const slotsToShow = firstBookedSlot
            ? slotsFromStart.filter((slot) => slot.end_time <= firstBookedSlot.start_time)
            : slotsFromStart;

        // Получаем уникальные времена окончания
        const availableEndTimes = slotsToShow
            .map((slot) => slot.end_time)
            .filter((time, index, self) => self.indexOf(time) === index) // Уникальные значения
            .sort();

        return createListCollection({
            items: availableEndTimes.map((time) => ({
                value: time,
                label: time,
            })),
        });
    }, [slots, selectedStartTime]);

    // Проверяем, забронирован ли слот с данным временем начала
    const isSlotBooked = (startTime: string): boolean => {
        if (!slots) return false;
        const slot = slots.find((s) => s.start_time === startTime);
        return slot ? !slot.is_available : false;
    };

    // Вычисляем продолжительность бронирования
    const bookingDuration = useMemo(() => {
        if (!selectedStartTime || !selectedEndTime) return null;

        return formatDuration(selectedStartTime, selectedEndTime, {
            hours: t.pages.office.hours,
            minutes: t.pages.office.minutes,
        });
    }, [selectedStartTime, selectedEndTime, t]);

    // Вычисляем стоимость бронирования
    const bookingCost = useMemo(() => {
        if (!selectedStartTime || !selectedEndTime || !room) return null;

        try {
            const { totalHours } = calculateTimeDifference(selectedStartTime, selectedEndTime);

            const totalCost = totalHours * room.price;

            return totalCost.toFixed(2);
        } catch {
            return null;
        }
    }, [selectedStartTime, selectedEndTime, room]);

    // Обработчик создания бронирования
    const handleBook = async () => {
        if (!roomId || !selectedDate || !selectedStartTime || !selectedEndTime) {
            return;
        }

        // Проверяем баланс перед бронированием
        if (bookingCost && customer) {
            const costValue = Number(bookingCost);
            if (customer.balance < costValue) {
                // Вычисляем, сколько не хватает
                const shortage = costValue - customer.balance;
                const shortageFormatted = shortage.toFixed(2);

                // Показываем модальное окно с предупреждением
                modalManager.show({
                    title: t.pages.office.insufficientFundsTitle,
                    message: t.pages.office.insufficientFundsMessageWithAmount.replace(
                        '{amount}',
                        shortageFormatted,
                    ),
                    size: 'md',
                    maxWidth: '400px',
                    buttons: [
                        {
                            type: 'secondary',
                            text: t.common.close,
                            onClick: () => {
                                modalManager.hide();
                            },
                        },
                        {
                            type: 'success',
                            text: t.pages.payment.replenishBalance,
                            onClick: () => {
                                modalManager.hide();
                                navigate('/payment');
                            },
                        },
                    ],
                });
                return;
            }
        }

        try {
            await createBooking({
                room_id: Number(roomId),
                date: selectedDate,
                start_time: selectedStartTime,
                end_time: selectedEndTime,
            }).unwrap();

            // Показываем успешный алерт
            alertManager.success(t.pages.office.bookingSuccess);

            // Навигируем на экран переговорных
            navigate('/office');
        } catch (error) {
            alertManager.error(t.pages.office.bookingError);
        }
    };

    // Проверяем, можно ли забронировать (все поля заполнены)
    const canBook = roomId && selectedDate && selectedStartTime && selectedEndTime;

    if (isCoworking && (isCustomerLoading || !canPreviewCoworking(customer?.id))) {
        return <LoadingState message={t.pages.support.loading} />;
    }

    if (!roomId) {
        return (
            <Box h="calc(100vh - 20px)" overflow="auto" bg="gray.100" pt={2} pb={{ base: 20, lg: 6 }}>
                <PageHeader title={t.pages.office.booking} />
                <Box maxW="4xl" mx="auto" px={4} pt={4}>
                    <Text color="gray.600">{t.pages.office.roomNotSelected}</Text>
                </Box>
            </Box>
        );
    }

    return (
        <Box h="calc(100vh - 20px)" overflow="auto" bg="gray.100" pt={2} pb={{ base: 20, lg: 6 }}>
            <PageHeader title={t.pages.office.booking} backPath={roomsPath} />
            <Box maxW="4xl" mx="auto" px={4}>
                <VStack gap={6} align="stretch" w={{ base: 'full', md: '600px' }} mx="auto">
                    {/* Превью переговорной */}
                    <MeetingRoomPreview roomId={roomId} />

                    {/* Новый механизм выбора через селекты */}
                    <VStack gap={6} align="stretch" w="full" p={6} bg="white">
                        {/* Селектор даты */}
                        <HStack justify="space-between" align="center">
                            <Text fontSize="md" fontWeight="bold" color="black" whiteSpace="nowrap" flex={1}>
                                {t.pages.office.selectDate}
                            </Text>
                            <Input
                                type="date"
                                value={selectedDate}
                                min={todayDate}
                                placeholder={t.pages.office.datePlaceholder}
                                onChange={(e) => {
                                    const newDate = e.target.value;
                                    // Проверяем, что выбранная дата не раньше сегодняшней
                                    if (newDate && newDate < todayDate) {
                                        return; // Не обновляем состояние, если дата в прошлом
                                    }
                                    setSelectedDate(newDate);
                                    // Сбрасываем выбор при смене даты
                                    setSelectedStartTime('');
                                    setSelectedEndTime('');
                                }}
                                fontSize="md"
                                color="black"
                                bg="yellow.600"
                                borderRadius={0}
                                w="190px"
                                maxW="300px"
                            />
                        </HStack>

                        {/* Селект "Начало" */}
                        <HStack justify="space-between" align="center">
                            <Text fontSize="md" fontWeight="bold" color="black" whiteSpace="nowrap" flex={1}>
                                {t.pages.office.startTime}:
                            </Text>
                            <Box flex={0}>
                                <BaseSelect
                                    collection={startTimeCollection}
                                    value={selectedStartTime}
                                    onValueChange={(value) => {
                                        setSelectedStartTime(value);
                                        setSelectedEndTime(''); // Сбрасываем конец при смене начала
                                    }}
                                    placeholder={t.pages.office.selectStartTime}
                                    w="190px"
                                    h="40px"
                                    isOptionLoading={isSlotsLoading}
                                    isOptionDisabled={(option) => isSlotBooked(option.value)}
                                    renderOptionLabel={(option) => {
                                        const booked = isSlotBooked(option.value);
                                        return (
                                            <HStack gap={2} align="center">
                                                <Text color={booked ? 'red.500' : 'black'}>
                                                    {option.label}
                                                </Text>
                                                {booked && (
                                                    <Text fontSize="xs" color="red.500">
                                                        ({t.pages.office.booked})
                                                    </Text>
                                                )}
                                            </HStack>
                                        );
                                    }}
                                    disabled={!selectedDate}
                                />
                            </Box>
                        </HStack>

                        {/* Селект "Конец" */}
                        <HStack justify="space-between" align="center" w="full">
                            <Text fontSize="md" fontWeight="bold" color="black" whiteSpace="nowrap" flex={1}>
                                {t.pages.office.endTime}:
                            </Text>
                            <Box flex={0} justifyContent="flex-end">
                                <BaseSelect
                                    collection={endTimeCollection}
                                    value={selectedEndTime}
                                    onValueChange={setSelectedEndTime}
                                    placeholder={t.pages.office.selectEndTime}
                                    w="190px"
                                    h="40px"
                                    disabled={!selectedStartTime}
                                />
                            </Box>
                        </HStack>

                        {/* Продолжительность и стоимость */}
                        {selectedStartTime && selectedEndTime && (bookingDuration || bookingCost) && (
                            <VStack
                                gap={2}
                                align="stretch"
                                pt={2}
                                borderTop="1px solid"
                                borderColor="gray.200"
                            >
                                {bookingDuration && (
                                    <HStack justify="space-between" align="center">
                                        <Text fontSize="md" fontWeight="bold" color="black">
                                            {t.pages.office.duration}:
                                        </Text>
                                        <Text fontSize="md" color="gray.700">
                                            {bookingDuration}
                                        </Text>
                                    </HStack>
                                )}
                                {bookingCost && (
                                    <HStack justify="space-between" align="center">
                                        <Text fontSize="md" fontWeight="bold" color="black">
                                            {t.pages.office.cost}:
                                        </Text>
                                        <Text fontSize="md" fontWeight="bold" color="yellow.600">
                                            €{bookingCost}
                                        </Text>
                                    </HStack>
                                )}
                            </VStack>
                        )}
                    </VStack>

                    {/* Кнопка "Забронировать" */}
                    <Button
                        buttonType="success"
                        onClick={handleBook}
                        w="full"
                        isLoading={isBookingLoading}
                        disabled={!canBook}
                    >
                        {t.pages.office.book}
                    </Button>
                </VStack>
            </Box>
        </Box>
    );
};

export default MeetingRoomBookingPage;
